import { TransactionType } from 'enum/transaction-type';
import { IUser } from './user';
import { Brokers } from 'enum/brokers';
import { ApiSearchResponseData } from './common';

export interface OrderItem {
  id: null | string;
  requestId: number;
  userId: string;
  brokerId: string;
  orderId: string;
  externalOrderId: string;
  broker: string;
  tradingSymbol: string;
  product: string;
  quantity: number;
  transactionType: string;
  price: number;
  triggerPrice: number;
  orderstatus: string;
  parentOrderId: null | string;
  tradedTime: string;
  user: null | any;
  orderStatus: string;
}

export interface Order {
  requestId: number;
  orderList: OrderItem[];
  placedBy: string;
  placedOn: string;
  price: number;
  quantity: number;
  stockName: string;
  stockId: string;
  transactionType: TransactionType;
  orderType: string;
  triggerPrice: number;
  timestamp: string;
}

export interface IOrderV2 {
  orderId: number;
  firstName: string;
  lastName: string;
  requestId: number;
  userId: string;
  brokerId: string;
  externalOrderId: string;
  broker: string;
  stockId: string;
  product: string;
  quantity: number;
  pendingQuantity: number;
  transactionType: string;
  price: number;
  triggerPrice: number;
  orderStatus: string;
  parentOrderId: any;
  nonDashBoardOrder: boolean;
  placedBy: any;
  stoploss: number;
  orderType: string;
  errorMessage: any;
  strategy: any;
  tradedTime: string;
  createdOn: string;
  user: IUser;
  stockName: string;
  nonDashboardOrder: boolean;
}

export interface OrderSearchResponse extends ApiSearchResponseData {
  searchResponse: IOrderV2[];
}

export enum OrderRequestStatus {
  OPEN = 'open',
  APPROVED = 'approved',
  CANCELLED = 'cancelled',
  REJECTED = 'rejected'
}

export enum OptionType {
  PE = 'PE',
  CE = 'CE'
}

/** Mirrors the server's OrderStatus enum — the broker's view of a placed order. */
export enum OrderExecutionStatus {
  OPEN = 'open',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  FAILED = 'failed',
  REJECTED = 'rejected',
  OTHERS = 'others',
  CREATED = 'created',
  TRIGGER_PENDING = 'trigger_pending'
}

const ORDER_STATUS_LABELS: Record<OrderExecutionStatus, string> = {
  [OrderExecutionStatus.OPEN]: 'Open',
  [OrderExecutionStatus.COMPLETED]: 'Executed',
  [OrderExecutionStatus.CANCELLED]: 'Cancelled',
  [OrderExecutionStatus.FAILED]: 'Failed',
  [OrderExecutionStatus.REJECTED]: 'Rejected',
  [OrderExecutionStatus.OTHERS]: 'Others',
  [OrderExecutionStatus.CREATED]: 'Placing',
  [OrderExecutionStatus.TRIGGER_PENDING]: 'Trigger Pending'
};

/** Human-readable label for a broker order status, e.g. 'completed' -> 'Executed'. */
export const getOrderStatusLabel = (orderStatus?: string | null): string | null =>
  orderStatus ? ORDER_STATUS_LABELS[orderStatus as OrderExecutionStatus] ?? orderStatus : null;

/**
 * An order the broker has finished with, one way or another. Anything else is
 * still in flight and worth polling for.
 */
export const isTerminalOrderStatus = (orderStatus?: string | null): boolean =>
  orderStatus === OrderExecutionStatus.COMPLETED ||
  orderStatus === OrderExecutionStatus.CANCELLED ||
  orderStatus === OrderExecutionStatus.FAILED ||
  orderStatus === OrderExecutionStatus.REJECTED;

/** Mirrors the server's OrderType enum. */
export type OrderTypeCode = 'MARKET' | 'LIMIT' | 'SL' | 'SLM';

const ORDER_TYPE_LABELS: Record<OrderTypeCode, string> = {
  MARKET: 'Market',
  LIMIT: 'Limit',
  SL: 'SL Limit',
  SLM: 'SL Market'
};

/** Human-readable label for an order type, e.g. 'SL' -> 'SL Limit'. */
export const getOrderTypeLabel = (orderType?: string | null): string | null =>
  orderType ? ORDER_TYPE_LABELS[orderType as OrderTypeCode] ?? orderType : null;

export interface OrderRequest {
  requestId: number;
  userId: string;
  quantity: number;
  stockId: string;
  product: string;
  transactionType: 'BUY' | 'SELL';
  price: number;
  adminId: string;
  stoploss: number;
  orderType: OrderTypeCode;
  triggerPrice: number;
  checkForBalance: boolean;
  orderId: number;
  scenarioDisplayName: string;
  scenario: string;
  orderRequestStatus: OrderRequestStatus;
  firstName: string | null;
  lastName: string | null;
  brokers: Brokers;
  createdOn: string;
  basketOrder: boolean;
  stockName: string;
  orderRequestList: OrderRequest[];
  optionType: OptionType;
  strikePrice: number;
  requiredMargin: number;
  simulationId: string;
  option: boolean;
  useMTF: boolean;
  accountUseMTF: boolean;
  insufficientFunds?: boolean;
  reasons?: string[];
  loggedIn?: boolean;
  /**
   * Execution details, populated by the server once the request is approved and
   * an order exists at the broker. Absent while the request is still pending.
   */
  orderStatus?: OrderExecutionStatus;
  executedQuantity?: number;
  /** Price the order actually traded at — the limit price is `price`. */
  executedPrice?: number;
  externalOrderId?: string;
  errorMessage?: string;
  executedOn?: string;
}

export interface StockPosition {
  stockId: string;
  quantity: number;
  price: number;
  /** 0 when the strategy set no stop loss. Trails as the position moves. */
  stopLossPrice?: number;
  /** 0 when the strategy set no target. */
  targetPrice?: number;
  pnl: number;
  lastTradedPrice: number;
  intraday: boolean;
  strikePrice: number;
  expectedQuantity: number;
  strategy: string;
  timestamp: string;
  user: IUser;
}

export type OrderPnlMap = Record<string, OrderPnl>;

export interface OrderPnl {
  orderId: number;
  ltp: number;
  pnl: number;
}
